import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {CarePlan} from '../models/careplan.model';
import {CPLocation} from '../models/location.model';
import {AbstractControl, FormGroup} from '@angular/forms';
import globals from '../../../config';
import { utcToZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class Utility {
  public screenWidth: number;
  public activityMenuState: string;
  private renderer: Renderer2;

  static random(min: number, max: number, round: number = 1): number {
    round = round === 0 ? 1 : Math.pow(10, round);

    return Math.round((Math.random() * (max - min) + min) * round) / round;
  }

  static cloneCarePlan(carePlan: CarePlan): CarePlan {
    const clone = new CarePlan();
    clone.id = carePlan.id;
    clone.isTemplate = carePlan.isTemplate;
    clone.taskList = carePlan.taskList;
    clone.tasks = carePlan.tasks;
    clone.parentTasks = carePlan.parentTasks;
    clone.carePlanType = carePlan.carePlanType;
    clone.diseaseId = carePlan.diseaseId;
    clone.diagnosisId = carePlan.diagnosisId;
    clone.deletedTaskIds = carePlan.deletedTaskIds;
    clone.desciption = carePlan.desciption;
    clone.duration = carePlan.duration;
    clone.name = carePlan.name;
    clone.cycles = carePlan.cycles;
    clone.img = carePlan.img;
    clone.startDate = carePlan.startDate;
    clone.startDateStr = carePlan.startDateStr;
    clone.ownerId = carePlan.ownerId;
    clone.owner = carePlan.owner;
    clone.patientId = carePlan.patientId;
    clone.usedTemplateId = carePlan.usedTemplateId;
    clone.durationWeeks =
      !carePlan.durationWeeks && !!carePlan.duration ? Math.floor(+carePlan.duration / 7) : carePlan.durationWeeks;

    return clone;
  }

  /**
   * Address Formatter
   *
   * @param {CPLocation, Address} location
   * @param {string} data default value if location doesnt exist
   *
   * @returns {string}
   */
  static addressFormatter(location: any, ...data: Array<string>): string {
    let locationModel = new CPLocation();

    let defaultValue = 'N/A';
    if (data && data.length) {
      defaultValue = data[0];
    }
    let address = defaultValue;
    if (location) {
      locationModel = {...locationModel, ...location};

      if (locationModel.address1) {
        // address1 exist
        address = locationModel.address1;

        if (locationModel.address2) {
          address += ` ${locationModel.address2}`;
        }

        if (locationModel.city) {
          address += `, ${locationModel.city}`;
        }

        if (locationModel.state) {
          address += `, ${locationModel.state}`;
        }

        if (locationModel.zipcode) {
          address += `, ${locationModel.zipcode}`;
        }

        if (locationModel.country) {
          address += `, ${locationModel.country}`;
        }
      } else if (!locationModel.address1) {
        // address1 doesnt exist
        if (locationModel.city && locationModel.state) {
          // city and state exist
          address = `${locationModel.city}, ${locationModel.state}`;
          if (locationModel.country) {
            address += `, ${locationModel.country}`;
          }
        } else if (!locationModel.city && locationModel.state) {
          // city doesnt exist but state exist
          address = locationModel.state;
          if (!!locationModel.country) {
            address += `, ${locationModel.country}`;
          }
        } else if (locationModel.city && !locationModel.state) {
          // city exist but state doesnt exist
          address = locationModel.city;
          if (locationModel.country) {
            address += `, ${locationModel.country}`;
          }
        } else if (!locationModel.city && !locationModel.state && locationModel.country) {
          address = `${locationModel.country}`;
        }
      }
    }

    return address;
  }

  static genderFormatter(gender: string): string {
    switch (gender) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
    }
    return 'N/A';
  }

  static getValidField(control: FormGroup | AbstractControl, field: string = '', isSubmitted: boolean): boolean {
    return control && field
      ? control.get(field) && control.get(field).invalid && isSubmitted
      : !field
      ? control.invalid && isSubmitted
      : false;
  }

  static isGoalReadOnly(goal): boolean {
    return goal.goalStatus ? !['ACTIVE', 'INACTIVE'].includes(goal.goalStatus) : false;
  }

  static goalHasDailyAssessment(taskTypeId, serviceTypeIds, goal): boolean {
    const DAILY_ASSESSMENT_ID = globals.SERVICE_TYPES.DAILY_ASSESSMENT;
    const tasksLength = goal.tasks.length;

    if (+taskTypeId === globals.TASKTYPES.ASSESSMENT && serviceTypeIds.includes(DAILY_ASSESSMENT_ID)) {
      for (let i = 0; i < tasksLength; i++) {
        if (+goal.tasks[i].serviceTypeId === DAILY_ASSESSMENT_ID) {
          return true;
        }
      }
    }

    return false;
  }

  static set(object: any, path: Array<string | number> | string, value: any): any | null {
    const fieldList = Array.isArray(path) ? path : path.split('.');
    const len = fieldList.length;

    for (let i = 0; i < len - 1; i++) {
      const field = fieldList[i];

      if (!object[field]) return null;
      object = object[field];
    }

    object[fieldList[len - 1]] = value;

    return value;
  }

  constructor(private rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.screenWidth = window.innerWidth;
  }

  public getScreenWidth(): number {
    return this.screenWidth;
  }

  public setActivityMenuState(data: string): void {
    this.activityMenuState = data;
  }

  public getActivityMenuState(): string {
    return this.activityMenuState;
  }

  // static cloneTask(task: CareplanTaskModel): CareplanTaskModel {
  //   let clone = new CareplanTaskModel();
  //   clone.id = task.id;
  //   clone.locationType = task.locationType;
  //   clone.location = task.location;
  //   clone.subtasks = task.subtasks;
  //   clone.dateCreated = task.dateCreated;
  //   clone.taskCarePlan = task.taskCarePlan;
  //   clone.performerId = task.performerId;
  //   clone.performerName = task.performerName;
  //   clone.deletedIds = task.deletedIds;
  //   clone.isExpanded = task.isExpanded;
  //   clone.isEdit = task.isEdit;
  //   clone.showWeeks = task.showWeeks;
  //   clone.showSubtask = task.showSubtask;
  //   clone.newlyAdded = task.newlyAdded;
  //   clone.offset = task.offset;
  //   clone.week = task.week;
  //   clone.name = task.name;
  //   clone.description = task.description;
  //   clone.usedTemplateId = task.usedTemplateId;
  //   clone.diagnoses = task.diagnoses;
  //   clone.startDate = task.startDate;
  //   clone.startDateStr = task.startDateStr;
  //   clone.startTime = task.startTime;
  //   clone.startTimeStr = task.startTimeStr;
  //   clone.dueDate = task.dueDate;
  //   clone.dueDateStr = task.dueDateStr;
  //   clone.dueTimeStr = task.dueTimeStr;
  //   clone.taskTypeId = task.taskTypeId;
  //   clone.taskType = task.taskType;
  //   clone.therapyTypeId = task.therapyTypeId;
  //   clone.therapyType = task.therapyType;
  //   clone.serviceTypeId = task.serviceTypeId;
  //   clone.serviceType = task.serviceType;
  //   clone.taskStatus = task.taskStatus;
  //   clone.numberRepeat = task.numberRepeat;
  //   clone.progress = task.progress;
  //
  //   if (!!task.dueDate) {
  //     clone.startDate = task.dueDate;
  //     clone.startDateStr = task.dueDateStr;
  //   }
  //   return clone;
  // }

  public css(el: HTMLElement, styles: {[key: string]: any}): void {
    for (const prop in styles) {
      if (styles.hasOwnProperty(prop)) {
        this.renderer.setStyle(el, prop, styles[prop]);
      }
    }
  }

  public getDistance(p1: google.maps.LatLngLiteral, p2: google.maps.LatLngLiteral) {
    const rad = (x) => (x * Math.PI) / 180;
    const R = 6378137; // Earth’s mean radius in meter
    const dLat = rad(p2.lat - p1.lat);
    const dLong = rad(p2.lng - p1.lng);
    const a =
      Math.pow(Math.sin(dLat / 2), 2) +
      Math.cos(rad(p1.lat)) * Math.cos(rad(p2.lat)) * Math.pow(Math.sin(dLong / 2), 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // returns the distance in km
  }

  // UTC to date for given time zone
  public static utcToZonedDate(utcTime:any, timeZoneId:string):Date {
    return utcToZonedTime(utcTime, timeZoneId);
  }

  // utc to time component for given timezone
  public static formatZonedTime(time:any, timeZoneId:string, timeFormat:string = null):string {
    timeFormat ||= "hh:mm a";
    return format(Utility.utcToZonedDate(time, timeZoneId), timeFormat);
  }

  // convert datetime in given timezone to utc
  public static zonedTimeToUTC(time:any, timeZoneId:string) {
    let dateTime = moment(time).format("YYYY-MM-DD HH:mm");
    let zonedDateTime =  moment.tz(dateTime, timeZoneId);
    let utcDateTime = moment.utc(zonedDateTime);
    return utcDateTime.valueOf();
  }

  public openInNewTab(event: any, href: string) {
    if (event.ctrlKey) {
      const a = document.createElement('a');

      a.setAttribute('target', '_blank');
      a.setAttribute('href', href);

      a.click();
    } else {
      window.location.href = href;
    }
  }

  public hasValue(value): boolean{
    return value !== undefined && value !== null &&
      (!Array.isArray(value) || value.length > 0) &&
      (typeof(value) !== 'string' || value.trim().length > 0);
  }
}
